import React, { useState } from 'react';
import { 
  FileText, 
  Download, 
  Calendar, 
  Euro, 
  CheckCircle2, 
  FileSpreadsheet,
  Building2,
  Users,
  Target,
  ClipboardList,
  PresentationIcon,
  ArrowLeft,
  File,
  FolderOpen,
  ChevronDown,
  ChevronRight,
  Clock,
  BookOpen,
  BarChart3,
  FileCheck,
  TrendingUp
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// DownloadButtons component buiten de hoofdcomponent voor betere performance
const DownloadButtons = ({ bestand, compact = false, onDownload }) => (
  <div className={`flex gap-2 ${compact ? '' : 'mt-2'}`}>
    <button
      onClick={() => onDownload(bestand, 'pdf')}
      className="flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
      data-testid={`download-pdf-${bestand.replace(/\//g, '-')}`}
    >
      <File className="w-4 h-4" />
      PDF
    </button>
    <button
      onClick={() => onDownload(bestand, 'docx')}
      className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
      data-testid={`download-word-${bestand.replace(/\//g, '-')}`}
    >
      <FileText className="w-4 h-4" />
      Word
    </button>
  </div>
);

const STOZDocumentenPage = () => {
  const [openSections, setOpenSections] = useState({
    hoofddocumenten: true,
    fase1: false,
    fase2: false,
    fase3: false,
    fase4: false,
    fase5: false,
    fase6: false,
    financieel: false,
    activiteiten: false,
    opschaling: false
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const downloadBestand = (bestandsnaam, type) => {
    const url = `/downloads/stoz/${bestandsnaam}.${type}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = `${bestandsnaam}.${type}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Chronologische structuur
  const projectStructuur = [
    {
      id: 'hoofddocumenten',
      titel: 'Hoofddocumenten',
      icon: BookOpen,
      kleur: 'emerald',
      beschrijving: 'Complete projectdocumentatie',
      documenten: [
        { naam: 'Volledig Onderzoeksopzet (Fase 1-6)', bestand: 'Onderzoeksopzet_STOZ_ZorgVleugel_2026_Fase1-6', beschrijving: 'Complete onderzoeksopzet met alle fases (~50 pagina\'s)' },
        { naam: 'Onderzoeksrapport', bestand: 'Onderzoeksrapport_STOZ_2026', beschrijving: 'Samenvatting strategietraject met bevindingen en conclusies' },
        { naam: 'Adviesrapport Digitalisering', bestand: 'Adviesrapport_Digitalisering_2026', beschrijving: 'Extern advies van Aelo' },
        { naam: 'Verantwoording Projectuitvoering', bestand: 'Verantwoording_projectuitvoering', beschrijving: 'RVO verantwoordingsdocument juni 2026' },
      ]
    },
    {
      id: 'fase1',
      titel: 'Fase 1: Analyse & Nulmeting',
      icon: ClipboardList,
      kleur: 'blue',
      maand: 'Januari 2026',
      beschrijving: 'Procesinventarisatie, interviews, nulmeting werkdruk',
      documenten: [
        { naam: 'Fase 1 Rapport', bestand: 'Fase1_Analyse_Nulmeting_januari2026', beschrijving: 'Volledige fase 1 documentatie' },
      ],
      subdocumenten: [
        { naam: '1. Procesinventarisaties', bestand: 'fase1_januari/Procesinventarisaties', beschrijving: 'Huidige werkprocessen in kaart' },
        { naam: '2. Interviews Verslagen', bestand: 'fase1_januari/Interviews_verslagen', beschrijving: 'Verslagen van stakeholder interviews' },
        { naam: '3. Documentanalyse', bestand: 'fase1_januari/Documentanalyse', beschrijving: 'Analyse bestaande documentatie' },
        { naam: '4. WLZ Procesanalyse', bestand: 'fase1_januari/WLZ_Procesanalyse', beschrijving: 'Analyse WLZ-specifieke processen' },
        { naam: '5. Nulmeting Werkdruk', bestand: 'fase1_januari/Nulmeting_werkdruk_administratieve_lasten', beschrijving: 'Nulmeting werkdruk en administratieve lasten' },
        { naam: '6. Knelpunten Overzicht', bestand: 'fase1_januari/Knelpunten_overzicht', beschrijving: 'Geïdentificeerde knelpunten' },
        { naam: '7. Output Proceskaart', bestand: 'fase1_januari/Output_proceskaart_nulmeting', beschrijving: 'Visuele proceskaart nulmeting' },
        { naam: 'Planning & Todo Januari', bestand: 'fase1_januari/Planning_todo_januari_2026', beschrijving: 'Planning en taken januari 2026' },
      ],
      deliverables: [
        { nr: 1, naam: 'Proceskaart huidige situatie', bestand: 'deliverables/Deliverable_1_Proceskaart' },
        { nr: 2, naam: 'Nulmeting werkdruk & administratieve lasten', bestand: 'deliverables/Deliverable_2_Nulmeting' },
      ],
      uren: [
        { naam: 'Januari 2026', bestand: 'urenregistratie/Uren_Januari_2026' }
      ]
    },
    {
      id: 'fase2',
      titel: 'Fase 2: Verkenning Oplossingen',
      icon: Target,
      kleur: 'purple',
      maand: 'Februari 2026',
      beschrijving: 'Marktverkenning, digitale volwassenheid, PoC specs',
      documenten: [
        { naam: 'Fase 2 Rapport', bestand: 'Fase2_Verkenning_februari2026', beschrijving: 'Volledige fase 2 documentatie' },
      ],
      deliverables: [
        { nr: 3, naam: 'Overzicht digitale opties', bestand: 'deliverables/Deliverable_3_Digitale_opties' },
      ],
      uren: [
        { naam: 'Februari 2026', bestand: 'urenregistratie/Uren_Februari_2026' }
      ]
    },
    {
      id: 'fase3',
      titel: 'Fase 3: Participatie & Draagvlak',
      icon: Users,
      kleur: 'green',
      maand: 'Maart 2026',
      beschrijving: 'Workshops, enquêtes, cliëntsessies, feedback',
      documenten: [
        { naam: 'Fase 3 Rapport', bestand: 'Fase3_Participatie_Draagvlak_maart2026', beschrijving: 'Volledige fase 3 documentatie' },
      ],
      deliverables: [
        { nr: 4, naam: 'Draagvlak rapport', bestand: 'deliverables/Deliverable_4_Draagvlak' },
      ],
      uren: [
        { naam: 'Maart 2026', bestand: 'urenregistratie/Uren_Maart_2026' }
      ]
    },
    {
      id: 'fase4',
      titel: 'Fase 4: Strategie & Scenario-ontwikkeling',
      icon: Building2,
      kleur: 'orange',
      maand: 'April 2026',
      beschrijving: 'Scenario\'s, kosten-baten, governance, PIA',
      documenten: [
        { naam: 'Fase 4 Rapport', bestand: 'Fase4_Strategie_Scenario_april2026', beschrijving: 'Strategie en scenario-ontwikkeling' },
      ],
      deliverables: [
        { nr: 5, naam: 'Strategisch kader', bestand: 'deliverables/Deliverable_5_Strategisch_kader' },
      ],
      uren: [
        { naam: 'April 2026', bestand: 'urenregistratie/Uren_April_2026' }
      ]
    },
    {
      id: 'fase5',
      titel: 'Fase 5: Strategievorming & KPI-set',
      icon: FileSpreadsheet,
      kleur: 'red',
      maand: 'Mei 2026',
      beschrijving: 'Verkennend kader, KPI-ontwerp, randvoorwaarden',
      documenten: [
        { naam: 'Fase 5 Rapport', bestand: 'Fase5_Strategievorming_KPI_mei2026', beschrijving: 'Strategievorming en KPI-ontwerp' },
      ],
      deliverables: [
        { nr: 6, naam: 'Strategievorming & KPI-set', bestand: 'deliverables/Deliverable_6_Strategievorming_KPIs' },
        { nr: 7, naam: 'KPI Set & monitoringkader', bestand: 'deliverables/Deliverable_7_KPI_set' },
      ],
      uren: [
        { naam: 'Mei 2026', bestand: 'urenregistratie/Uren_Mei_2026' }
      ]
    },
    {
      id: 'fase6',
      titel: 'Fase 6: Eindrapportage',
      icon: PresentationIcon,
      kleur: 'teal',
      maand: 'Juni 2026',
      beschrijving: 'Eindrapport, presentaties, lessons learned',
      documenten: [
        { naam: 'Fase 6 Rapport', bestand: 'Fase6_Eindrapportage_juni2026', beschrijving: 'Volledige fase 6 documentatie' },
      ],
      deliverables: [
        { nr: 8, naam: 'Eindrapport strategievorming', bestand: 'deliverables/Deliverable_8_Eindrapport' },
      ],
      uren: [
        { naam: 'Juni 2026', bestand: 'urenregistratie/Uren_Juni_2026' }
      ]
    },
    {
      id: 'financieel',
      titel: 'Financiële Verantwoording',
      icon: Euro,
      kleur: 'yellow',
      beschrijving: 'Urenregistratie, budget en RVO-verantwoording',
      documenten: [
        { naam: 'Financiële Verantwoording', bestand: 'Financiele_Verantwoording_STOZ_2026', beschrijving: 'Complete urenregistratie en budgetverantwoording' },
      ],
      extra: [
        { naam: 'Financieel Overzicht (Totaal vs Begroot)', bestand: 'urenregistratie/Financieel_overzicht' },
        { naam: 'Uren Ilias el Ghamarti (Directeur)', bestand: 'urenregistratie/Uren_Ilias_el_Ghamarti' },
        { naam: 'Uren Samra Benhamouch (Projectcoördinator)', bestand: 'urenregistratie/Uren_Samra_Benhamouch' },
        { naam: 'Uren Begeleiders (Geaggregeerd)', bestand: 'urenregistratie/Uren_Begeleiders' },
        { naam: 'Uren Zorgcoördinator (Ellen B.)', bestand: 'urenregistratie/Uren_Zorg_Coordinator' },
        { naam: 'Offerte en Factuur Externe Adviseur (Aelo)', bestand: 'Offerte_factuur_externe_adviseur' },
      ]
    },
    {
      id: 'activiteiten',
      titel: 'Projectactiviteiten & Communicatie',
      icon: Calendar,
      kleur: 'cyan',
      beschrijving: 'Uitgevoerde activiteiten, communicatie en enquêtes',
      documenten: [
        { naam: 'Uitgevoerde Activiteiten per Maand', bestand: 'Uitgevoerde_activiteiten_per_maand', beschrijving: 'Alle projectactiviteiten januari - juni 2026' },
        { naam: 'Activiteiten en Communicatie (Overzicht)', bestand: 'Activiteiten_en_communicatie', beschrijving: 'Interne/externe communicatie en stakeholders' },
        { naam: 'Communicatie naar Medewerkers', bestand: 'Communicatie_naar_medewerkers', beschrijving: '6 nieuwsbrieven januari - juni 2026' },
        { naam: 'Uitnodigingen Workshops en Sessies', bestand: 'Uitnodigingen_workshops_sessies', beschrijving: 'Alle uitnodigingen voor bijeenkomsten' },
        { naam: 'Enquêteresultaten', bestand: 'Enqueteresultaten', beschrijving: 'Nulmeting, draagvlak en evaluatie' },
      ]
    },
    {
      id: 'opschaling',
      titel: 'Opschalings- en Evaluatieroute',
      icon: TrendingUp,
      kleur: 'pink',
      beschrijving: 'Documenten voor opschaling naar €750.000 (3 jaar)',
      documenten: [
        { naam: 'Opschalingsplan 2026-2029', bestand: 'opschaling/Opschalingsplan_STOZ_2026-2029', beschrijving: 'Volledig projectplan voor 36 maanden opschaling' },
        { naam: 'Evaluatiekader & Waardebepaling', bestand: 'opschaling/Evaluatiekader_Waardebepaling', beschrijving: 'SROI-analyse en KPI-framework' },
        { naam: 'Verklaring Samenwerking', bestand: 'opschaling/Verklaring_Samenwerking_Financiering', beschrijving: 'Samenwerkingsverklaring met 5 aanbieders + inkoper' },
        { naam: 'Kennisdelingsplan', bestand: 'opschaling/Kennisdelingsplan', beschrijving: 'Congressen, webinars, Digizo.nu' },
        { naam: 'Gedetailleerde Begroting', bestand: 'opschaling/Gedetailleerde_Begroting', beschrijving: '€750.000 opgesplitst per kostenpost' },
        { naam: 'Bewijs Eerdere Inzet', bestand: 'opschaling/Bewijs_Eerdere_Inzet_Impact', beschrijving: '60+ cliënten bij 3+ aanbieders' },
        { naam: 'STOZ Voorwaarden Checklist', bestand: 'opschaling/STOZ_Voorwaarden_Checklist', beschrijving: 'Compliance check alle RVO-eisen' },
        { naam: 'Onderbouwing Subsidievoorwaarden', bestand: 'opschaling/Onderbouwing_Subsidievoorwaarden_STOZ', beschrijving: 'Onderbouwing infrastructuur vs. processen (NEN7510, AVG)' },
      ]
    }
  ];

  const getKleurClasses = (kleur) => {
    const kleuren = {
      emerald: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', text: 'text-emerald-400', icon: 'text-emerald-400' },
      blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-400', icon: 'text-blue-400' },
      purple: { bg: 'bg-purple-500/20', border: 'border-purple-500/40', text: 'text-purple-400', icon: 'text-purple-400' },
      green: { bg: 'bg-green-500/20', border: 'border-green-500/40', text: 'text-green-400', icon: 'text-green-400' },
      orange: { bg: 'bg-orange-500/20', border: 'border-orange-500/40', text: 'text-orange-400', icon: 'text-orange-400' },
      red: { bg: 'bg-red-500/20', border: 'border-red-500/40', text: 'text-red-400', icon: 'text-red-400' },
      teal: { bg: 'bg-teal-500/20', border: 'border-teal-500/40', text: 'text-teal-400', icon: 'text-teal-400' },
      yellow: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/40', text: 'text-yellow-400', icon: 'text-yellow-400' },
      pink: { bg: 'bg-pink-500/20', border: 'border-pink-500/40', text: 'text-pink-400', icon: 'text-pink-400' },
      cyan: { bg: 'bg-cyan-500/20', border: 'border-cyan-500/40', text: 'text-cyan-400', icon: 'text-cyan-400' },
    };
    return kleuren[kleur] || kleuren.emerald;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 mb-6"
          >
            <a 
              href="/"
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Terug naar Dashboard</span>
            </a>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-2">
              ZorgVleugel: Samen vooruit in hybride zorg
            </h1>
            <p className="text-lg text-white/90">
              STOZ Subsidienummer: STOZ25-03866622 | Toekenning: 7 januari 2026
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <Euro className="w-6 h-6 mb-2 text-yellow-300" />
              <div className="text-2xl font-bold">€25.000</div>
              <div className="text-sm text-white/70">Subsidie</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <Calendar className="w-6 h-6 mb-2 text-blue-300" />
              <div className="text-2xl font-bold">6 maanden</div>
              <div className="text-sm text-white/70">Jan - Jun 2026</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <FileCheck className="w-6 h-6 mb-2 text-green-300" />
              <div className="text-2xl font-bold">8</div>
              <div className="text-sm text-white/70">Deliverables</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <CheckCircle2 className="w-6 h-6 mb-2 text-emerald-300" />
              <div className="text-2xl font-bold">43+</div>
              <div className="text-sm text-white/70">Documenten</div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Inhoudsopgave */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-6"
        >
          <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-emerald-400" />
            Snelle Navigatie
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {projectStructuur.map((sectie) => (
              <button
                key={sectie.id}
                onClick={() => {
                  setOpenSections(prev => ({ ...prev, [sectie.id]: true }));
                  document.getElementById(sectie.id)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-left text-sm text-slate-300 hover:text-white hover:bg-slate-700/50 px-3 py-2 rounded-lg transition-colors"
              >
                {sectie.maand ? `${sectie.maand.split(' ')[0]}` : ''} {sectie.titel.replace('Fase ', 'F').replace(': ', ' - ')}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Chronologische Secties */}
        <div className="space-y-4">
          {projectStructuur.map((sectie, index) => {
            const Icon = sectie.icon;
            const kleuren = getKleurClasses(sectie.kleur);
            const isOpen = openSections[sectie.id];

            return (
              <motion.div
                key={sectie.id}
                id={sectie.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className={`border rounded-xl overflow-hidden ${kleuren.border} ${kleuren.bg}`}
              >
                {/* Sectie Header - Klikbaar */}
                <button
                  onClick={() => toggleSection(sectie.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                  data-testid={`toggle-${sectie.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-slate-800/50`}>
                      <Icon className={`w-5 h-5 ${kleuren.icon}`} />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white">{sectie.titel}</h3>
                        {sectie.maand && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${kleuren.bg} ${kleuren.text} border ${kleuren.border}`}>
                            {sectie.maand}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-400">{sectie.beschrijving}</p>
                    </div>
                  </div>
                  {isOpen ? (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  )}
                </button>

                {/* Sectie Content */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-slate-700/50"
                    >
                      <div className="p-4 space-y-4">
                        {/* Hoofddocumenten */}
                        {sectie.documenten && sectie.documenten.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              {sectie.id === 'hoofddocumenten' ? 'Documenten' : 'Fase Rapport'}
                            </h4>
                            <div className="space-y-2">
                              {sectie.documenten.map((doc) => (
                                <div key={doc.bestand} className="bg-slate-800/50 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                  <div>
                                    <div className="font-medium text-white">{doc.naam}</div>
                                    {doc.beschrijving && (
                                      <div className="text-xs text-slate-400">{doc.beschrijving}</div>
                                    )}
                                  </div>
                                  <DownloadButtons bestand={doc.bestand} compact onDownload={downloadBestand} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Subdocumenten (voor fase1) */}
                        {sectie.subdocumenten && sectie.subdocumenten.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                              <FolderOpen className="w-4 h-4 text-blue-400" />
                              Analyse Documenten (Januari)
                            </h4>
                            <div className="grid gap-2">
                              {sectie.subdocumenten.map((doc) => (
                                <div key={doc.bestand} className="bg-slate-800/50 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                  <div>
                                    <div className="font-medium text-white">{doc.naam}</div>
                                    {doc.beschrijving && (
                                      <div className="text-xs text-slate-400">{doc.beschrijving}</div>
                                    )}
                                  </div>
                                  <DownloadButtons bestand={doc.bestand} compact onDownload={downloadBestand} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Deliverables */}
                        {sectie.deliverables && sectie.deliverables.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-green-400" />
                              Deliverables
                            </h4>
                            <div className="space-y-2">
                              {sectie.deliverables.map((del) => (
                                <div key={del.bestand} className="bg-slate-800/50 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <span className="bg-green-500 text-white text-xs font-bold w-6 h-6 rounded flex items-center justify-center">
                                      {del.nr}
                                    </span>
                                    <span className="text-white">{del.naam}</span>
                                  </div>
                                  <DownloadButtons bestand={del.bestand} compact onDownload={downloadBestand} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Urenregistratie */}
                        {sectie.uren && sectie.uren.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                              <Clock className="w-4 h-4 text-orange-400" />
                              Urenregistratie
                            </h4>
                            <div className="space-y-2">
                              {sectie.uren.map((uur) => (
                                <div key={uur.bestand} className="bg-slate-800/50 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                  <span className="text-white">{uur.naam}</span>
                                  <DownloadButtons bestand={uur.bestand} compact onDownload={downloadBestand} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Extra documenten (voor financieel) */}
                        {sectie.extra && sectie.extra.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                              <BarChart3 className="w-4 h-4 text-yellow-400" />
                              Overzichten per Medewerker
                            </h4>
                            <div className="space-y-2">
                              {sectie.extra.map((doc) => (
                                <div key={doc.bestand} className="bg-slate-800/50 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                  <span className="text-white">{doc.naam}</span>
                                  <DownloadButtons bestand={doc.bestand} compact onDownload={downloadBestand} />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Alles Openen/Sluiten */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-6 flex justify-center gap-4"
        >
          <button
            onClick={() => setOpenSections(Object.keys(openSections).reduce((acc, key) => ({ ...acc, [key]: true }), {}))}
            className="text-sm text-slate-400 hover:text-white px-4 py-2 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
          >
            Alles openen
          </button>
          <button
            onClick={() => setOpenSections(Object.keys(openSections).reduce((acc, key) => ({ ...acc, [key]: false }), {}))}
            className="text-sm text-slate-400 hover:text-white px-4 py-2 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
          >
            Alles sluiten
          </button>
        </motion.div>

        {/* Footer Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 text-center text-slate-500 text-sm pb-8"
        >
          <p>STOZ Project Zorg-Vleugel | Januari - Juni 2026</p>
          <p className="mt-1">Alle documenten zijn RVO-proof en voldoen aan de verantwoordingseisen</p>
        </motion.div>
      </div>
    </div>
  );
};

export default STOZDocumentenPage;
